import type { Memory } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `memory` — the Memory page (PRD-18 AGM-090…093).
 *
 * Moved here from `components/memory/memory-strings.ts` (PRD-27 LOC-010). The pure grouping
 * module beside it — `memory-grouping.ts` — stays where it is and keeps taking a labels pack
 * as a parameter, which is the in-repo model for a module with no component body to read
 * `useI18n()` from (LOC-012).
 *
 * The two lines this page has to get right are the same two the listing page discloses, and
 * they are written to match it word for word: an agent "uses preferences you have saved"
 * (global, true of every agent regardless of what its maker chose) and "learns new
 * preferences here" (that agent's own, and only that agent's). A person who reads the About
 * panel and then comes here must not find a third story about how memory works.
 *
 * "Everywhere" is the label for the global partition rather than "Global" or "Platform":
 * it answers the question the reader actually has, which is where this preference will show
 * up, not which subsystem owns it.
 *
 * Entries that count something take the reader's formatters and declare every plural form
 * they need, rather than comparing the number to 1 (LOC-015). English is invariant in both
 * of the entries below, which is exactly why the forms have to be written out: a translator
 * cannot see a distinction that only the other language makes.
 *
 * English is the source of truth AND the type (LOC-011): `Messages = typeof en`, so the
 * Portuguese sibling is checked against the shape of this file and a missing key is a
 * compile error rather than a runtime fallback.
 */

/**
 * The slice of `useI18n()` a counting entry here needs, threaded in from the page.
 *
 * Structural rather than `Pick<I18n, …>`: this file is imported by `messages/en/index.ts`,
 * which `I18nProvider` imports in turn, so naming `I18n` here would close that circle for no
 * gain.
 */
type Counting = { locale: Locale; formatCount: (value: number) => string }

/**
 * Picks the form CLDR says this count takes.
 *
 * Never `count === 1`: `Intl.PluralRules('pt-BR').select(0)` returns `one`, so Portuguese
 * takes the singular at zero and a two-branch comparison is already wrong in one of the two
 * shipped languages, on every empty state. `other` is required and is the fallback, so a
 * locale with `few`/`many` can add them here without touching a call site.
 */
const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

/**
 * What the three kinds of preference are called, as words rather than as identifiers.
 *
 * `satisfies` over the contract enum is the point (PRD-27 D-9, LOC-018): a fourth category
 * added to `Memory` becomes a compile error in both catalogues rather than a blank badge on
 * a row nobody notices. The page also enumerates these keys to build its `<select>`, so the
 * order here is the order the options appear in.
 */
const categories = {
  output_preference: 'How you like answers',
  domain_context: 'Context about your work',
  working_style: 'How you like to work'
} satisfies Record<Memory['category'], string>

export const memory = {
  title: 'Memory',
  lead: 'Preferences the assistant keeps for you. You can edit or remove any of them, and a change takes effect on your next message.',

  /** The honest two-line explanation, mirroring the listing page's disclosure panel. */
  whereTitle: 'Where each one applies',
  whereGlobal: 'Preferences you save here apply everywhere. Every agent can use them.',
  whereAgent:
    'Preferences an agent learns while you talk to it stay with that agent. No other agent can see or use them.',

  everywhere: 'Everywhere',
  /** Named as a restriction, not as an owner: "Atlas" alone would read as a source, not a limit. */
  agentOnly: (name: string): string => `Only in ${name}`,

  addCategoryLabel: 'Category',
  addStatementLabel: 'What to remember',
  addPlaceholder: 'e.g. Always give me the short answer first, then the detail.',
  add: 'Add',
  addHint: 'Anything you add here applies everywhere.',

  filterLabel: 'Show',
  filterAll: 'All preferences',
  filterGlobal: 'Everywhere only',
  filterCount: (fmt: Counting, label: string, count: number): string => `${label} (${fmt.formatCount(count)})`,

  listTitle: 'Saved preferences',
  /**
   * Deliberately "shown" rather than "saved": the list is filtered, and a total that quietly
   * ignored the active filter would tell someone they have three preferences when they have
   * thirty, two of which this agent uses.
   *
   * English has one form here — "shown" is a participle and does not inflect — and Portuguese
   * has two, because the participle agrees with *preferências*. Both are declared so the
   * second locale has somewhere to put its agreement instead of inheriting English's silence.
   */
  listCount: (fmt: Counting, count: number): string =>
    plural(fmt, count, {
      one: `${fmt.formatCount(count)} shown`,
      other: `${fmt.formatCount(count)} shown`
    }),

  suggested: 'Suggested — confirm to use',
  confirm: 'Confirm',
  confirming: 'Confirming…',
  confirmHint: 'Confirming means this is true about you. Until then it is not used in any answer.',
  confirmFailed: 'That could not be confirmed. Try again.',

  enable: 'Use this preference',
  disable: 'Stop using this preference',
  forget: 'Forget this',
  /**
   * `when` arrives already formatted, and the sentence deliberately adds no preposition of
   * its own: Portuguese `RelativeTimeFormat` carries one already ("há 5 min."), so "added on
   * há 5 min." is what a helpful-looking "added on" would produce (LOC-022).
   */
  added: (when: string): string => `added ${when}`,
  /** The multiplication sign is a symbol, not a word, so only the number localizes. */
  used: (fmt: Counting, count: number): string => `used ${fmt.formatCount(count)}×`,

  categories,

  empty: {
    title: 'Nothing remembered yet',
    body: 'Add something above, or just tell an agent to remember it during a conversation.'
  },
  emptyFiltered: {
    title: 'Nothing here',
    body: 'No preferences match this filter. Switch back to all preferences to see the rest.'
  }
}
